import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { UserForm } from '@/components/forms/UserForm'
import { usersApi } from '@/api/users'
import type { UpdateUserInput } from '@/types/user'
import { PageLoading } from '@/components/PageLoading'
import { getApiErrorMessage } from '@/utils/error'

export const UserEdit = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Fetch user data
  const { data: userData, isLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersApi.getUser(id!),
    enabled: !!id,
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (values: UpdateUserInput) => usersApi.updateUser(id!, values),
    onSuccess: () => {
      message.success('User updated successfully')
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['user', id] })
      navigate(`/users/${id}`)
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to update user'))
    },
  })

  const handleSubmit = (values: UpdateUserInput) => {
    updateMutation.mutate(values)
  }

  const handleCancel = () => {
    navigate(`/users/${id}`)
  }

  if (isLoading) {
    return <PageLoading />
  }

  return (
    <div>
      <h1>Edit User</h1>
      <UserForm
        initialValues={userData?.data}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={updateMutation.isPending}
        isEdit
      />
    </div>
  )
}
